import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../api';

const KURS_TYPEN = [
  { value: 'pirat',         label: '🏴‍☠️ Pirat' },
  { value: 'seepferdchen',  label: '🐴 Seepferdchen' },
  { value: 'trixi',         label: '🌟 Trixi' },
  { value: 'bronze',        label: '🥉 Bronze' },
  { value: 'silber',        label: '🥈 Silber' },
  { value: 'gold',          label: '🥇 Gold' },
  { value: 'hai',           label: '🦈 Hai (Leistungsabzeichen)' },
  { value: 'la_silber',     label: '🦈🥈 Leistungsabzeichen Silber' },
  { value: 'la_gold',       label: '🦈🥇 Leistungsabzeichen Gold' },
];

export default function GruppenForm() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState({
    kurs_typ: searchParams.get('typ') || 'seepferdchen',
    name: '', trainer_id: '', ort: '',
    max_teilnehmer: 15, aktiv: true, bemerkung: '',
  });
  const [trainerListe, setTrainerListe] = useState([]);
  const [loading, setLoading]   = useState(isEdit);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    api.get('/api/admin/trainer-benutzer')
      .then(r => setTrainerListe(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/api/admin/gruppen/${id}`)
      .then(r => setForm({
        kurs_typ:       r.data.kurs_typ || 'seepferdchen',
        name:           r.data.name || '',
        trainer_id:     r.data.trainer_id || '',
        ort:            r.data.ort || '',
        max_teilnehmer: r.data.max_teilnehmer || 15,
        aktiv:          r.data.aktiv ?? true,
        bemerkung:      r.data.bemerkung || '',
      }))
      .catch(() => setError('Gruppe konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name ist Pflichtfeld'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...form, trainer_id: form.trainer_id || null };
      if (isEdit) await api.put(`/api/admin/gruppen/${id}`, payload);
      else        await api.post('/api/admin/gruppen', payload);
      navigate('/gruppen');
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Speichern');
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '2rem' }}>
      <div className="loading-spinner" style={{ margin: '0 auto' }} />
    </div>
  );

  return (
    <div style={{ maxWidth: 640 }}>
      <div className="page-header">
        <h2 className="page-title">{isEdit ? 'Gruppe bearbeiten' : 'Neue Ausbildungsgruppe'}</h2>
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/gruppen')}>← Zurück</button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card" style={{ padding: '1.5rem' }}>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label required">Ausbildungsstufe</label>
            <select className="form-select" value={form.kurs_typ}
              onChange={e => set('kurs_typ', e.target.value)} disabled={isEdit}>
              {KURS_TYPEN.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>
            {isEdit && (
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                Ausbildungsstufe kann nachträglich nicht geändert werden
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label required">Gruppenname</label>
            <input type="text" className="form-input" value={form.name}
              onChange={e => set('name', e.target.value)}
              placeholder="z. B. Seepferdchen Gruppe A" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Trainer/in</label>
              <select className="form-select" value={form.trainer_id}
                onChange={e => set('trainer_id', e.target.value)}>
                <option value="">– Kein Trainer –</option>
                {trainerListe.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.vorname} {t.nachname}
                  </option>
                ))}
              </select>
              {trainerListe.length === 0 && (
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                  Noch keine Trainer-Benutzerkonten vorhanden
                </div>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Ort / Bahn</label>
              <input type="text" className="form-input" value={form.ort}
                onChange={e => set('ort', e.target.value)}
                placeholder="z. B. Hallenbad, Bahn 3" />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label className="form-label">Max. Teilnehmer</label>
              <input type="number" className="form-input" value={form.max_teilnehmer}
                min={1} max={50}
                onChange={e => set('max_teilnehmer', parseInt(e.target.value) || 15)} />
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', paddingTop: '1.75rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.aktiv}
                  onChange={e => set('aktiv', e.target.checked)} />
                <span>Gruppe ist aktiv</span>
              </label>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Bemerkung</label>
            <textarea className="form-control" rows={3} value={form.bemerkung}
              onChange={e => set('bemerkung', e.target.value)}
              placeholder="Interne Notizen zur Gruppe…" />
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
            <button type="button" className="btn btn-secondary"
              onClick={() => navigate('/gruppen')}>Abbrechen</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Speichern…' : isEdit ? 'Änderungen speichern' : 'Gruppe anlegen'}
            </button>
          </div>
        </form>
      </div>

      {isEdit && (
        <div className="card" style={{ padding: '1rem', marginTop: '1rem' }}>
          <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
            💡 Trainingstermine (Wochentag + Uhrzeit) werden auf der Gruppendetailseite gepflegt.
          </div>
        </div>
      )}
    </div>
  );
}
